import React, { useState } from 'react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';

const ChevronDown = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const ShieldCheck = ({ className }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const Database = ({ className }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <ellipse cx="12" cy="5" rx="9" ry="3" />
    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
  </svg>
);

const Cookie = ({ className }) => (
    <svg 
        xmlns="http://www.w3.org/2000/svg" 
        width="24" 
        height="24" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        className={className}
    >
        <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5" />
        <path d="M8.5 8.5v.01" />
        <path d="M16 15.5v.01" />
        <path d="M12 12v.01" />
        <path d="M15.5 16v.01" />
        <path d="M8.5 15.5v.01" />
    </svg>
);

function AccordionItem({ title, icon, children, isOpen, onToggle }) {
  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        onClick={onToggle}
        aria-expanded={isOpen}
        className="accordion-button flex w-full items-center justify-between py-5 md:py-6 text-left"
      >
        <span className="flex items-center">
          {icon && React.cloneElement(icon, { className: "w-5 h-5 mr-3 text-indigo-600" })}
          <span className="text-lg font-semibold text-gray-900">{title}</span>
        </span>
        <ChevronDown 
            className={`accordion-icon w-5 h-5 text-gray-500 transition-transform duration-300 ${isOpen ? 'transform rotate-180' : ''}`} 
        />
      </button>

      <div
        className={`grid overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <div className="pb-6 text-gray-600 leading-relaxed space-y-4">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PrivacyPolicyPage() {
  const [openIndexes, setOpenIndexes] = useState([]);

  const handleToggle = (index) => {
    setOpenIndexes(prevIndexes => 
      prevIndexes.includes(index) 
        ? prevIndexes.filter(i => i !== index) 
        : [...prevIndexes, index]
    );
  };

  const policySections = [
    {
      title: "Types of Information Collected",
      icon: <Database />,
      content: (
        <>
          <p>We may collect the following information:</p>
          <ul className="list-disc list-inside pl-4 space-y-1">
            <li><strong>Personal Information:</strong> Name, email, phone number, address</li>
            <li><strong>Usage Information:</strong> Log files, browser type, IP address</li>
            <li><strong>Technical Information:</strong> Device information, operating system, cookies</li>
          </ul>
        </>
      )
    },
    {
      title: "Purpose of Information Collection",
      icon: <ShieldCheck />,
      content: (
        <>
          <p>Techvelly collects information for the following purposes:</p>
          <ul className="list-disc list-inside pl-4 space-y-1">
            <li>Providing and customizing services</li>
            <li>Client support and communication</li>
            <li>Service development and security assurance</li>
            <li>Fulfilling legal obligations</li>
          </ul>
        </>
      )
    },
    {
      title: "Information Sharing Policy",
      icon: <Cookie />,
      content: (
        <>
          <p>We do not share your information with third parties unless:</p>
          <ul className="list-disc list-inside pl-4 space-y-1">
            <li>You provide your consent</li>
            <li>There is a legal obligation</li>
            <li>There is a merger/sale of our company or services</li>
          </ul>
        </>
      )
    },
    {
      title: "Cookies Policy",
      icon: <Cookie />,
      content: (
        <p>Our website uses cookies to improve the user experience. You can disable cookies through your browser settings, but some features may not work.</p>
      )
    },
    {
      title: "Data Security Measures",
      icon: <ShieldCheck />,
      content: (
        <>
          <p>Techvelly adopts the following security measures:</p>
          <ul className="list-disc list-inside pl-4 space-y-1">
            <li>SSL encryption</li>
            <li>Database access control</li>
            <li>Regular system updates and monitoring</li>
            <li>Restricted information access to specific personnel</li>
          </ul>
        </>
      )
    },
    {
      title: "Data Retention Policy",
      icon: <Database />,
      content: (
        <>
          <ul className="list-disc list-inside pl-4 space-y-1">
            <li>User information is retained as long as necessary.</li>
            <li>Information may be kept for longer periods for legal or accounting purposes.</li>
            <li>Upon termination of the agreement, a request to delete/export data may be made, if necessary.</li>
          </ul>
        </>
      )
    },
    {
      title: "User Rights",
      icon: <ShieldCheck />,
      content: (
        <>
          <ul className="list-disc list-inside pl-4 space-y-1">
            <li>You may request to view, correct, or delete your information.</li>
            <li>You may apply restrictions on the use of your personal information.</li>
            <li>You may opt-out of cookies or marketing communications.</li>
          </ul>
        </>
      )
    },
    {
      title: "Third-Party Links or Services",
      icon: <Cookie />,
      content: (
        <p>Our website may contain links to third parties. We are not responsible for their privacy policies.</p>
      )
    },
    {
      title: "Children's Privacy",
      icon: <ShieldCheck />,
      content: (
        <p>Our services are not intended for children under 13 years of age. If we collect information from a minor user, we will delete it.</p>
      )
    }
  ];

  return (
    <>
    <Header />
    <div className="bg-gray-50 text-gray-800 min-h-screen font-sans">
      <div className="container mx-auto max-w-4xl px-4 py-12 md:py-20">
        
        <header className="mb-10 md:mb-16 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 mt-10">
            Privacy Policy
          </h1>
          <p className="text-lg text-gray-600">
            Your privacy is important to us. It's our policy to respect your privacy regarding any information we may collect from you.
          </p>
          <p className="text-sm text-gray-500 mt-4">
            Effective Date: May 23, 2025
          </p>
        </header>

        <div className="bg-white p-6 md:p-10 rounded-2xl shadow-lg">
          
          <div className="pb-6 border-b border-gray-200">
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">Introduction</h2>
            <p className="text-gray-600 leading-relaxed">
              Welcome to Techvelly. We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains what information we collect, how we use it, and your rights in relation to it. If you have any questions or concerns about this privacy notice, or our practices with regards to your personal information, please contact us.
            </p>
          </div>

          <div className="mt-6">
            {policySections.map((section, index) => (
              <AccordionItem
                key={index}
                title={section.title}
                icon={section.icon}
                isOpen={openIndexes.includes(index)}
                onToggle={() => handleToggle(index)}
              >
                {section.content}
              </AccordionItem>
            ))}
          </div>

          <div className="pt-8 mt-8 border-t border-gray-200">
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">Policy Changes</h2>
            <p className="text-gray-600 leading-relaxed">
              Techvelly may update this policy at any time. Changes will be published on the website and you will be notified via email if necessary.
            </p>
          </div>

          <div className="pt-8 mt-8 border-t border-gray-200">
             <h2 className="text-2xl font-semibold text-gray-900 mb-3">Contact Us</h2>
             <p className="text-gray-600 leading-relaxed">
                If you have any questions about this Privacy Policy, please contact us at:
             </p>
             <ul className="list-disc list-inside pl-4 space-y-1">
                <li>Email: <a href="mailto:info@techvelly.com" className="text-indigo-600 hover:text-indigo-700 font-medium transition-colors">info@techvelly.com</a></li>
                <li>Mobile: +8801927-046394</li>
                <li>Website: <a href="https://www.techvelly.com" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-700 font-medium transition-colors">www.techvelly.com</a></li>
              </ul>
          </div>

        </div>
      </div>
    </div>
    <Footer />
    </>
  );
}